import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaUtensils, FaArrowUp } from 'react-icons/fa';
import { Outlet } from 'react-router-dom';
import { useBranch } from '~/contexts/BranchContext';
import { useShoppingCart } from '~/contexts/ShoppingCartContext';
import { getCategoryName, getCategoryDescription } from '~/utils/categoryHelper';
import { getDirectAddPayload, getItemName, getItemDescription } from '~/utils/menuItemHelper';
import { saveItemCardsToSession } from '~/components/modal/MenuItemDetails/utils/sessionUtils';
import type { MenuItem, MainCategory } from '~/types/new_types';

// Custom hooks
import { useScrollDetection } from '~/hooks/useScrollDetection';
import { useScrollNavigation } from '~/hooks/useScrollNavigation';
import { useDragScroll } from '~/hooks/useDragScroll';

// Section components
import {
  LazyCategorySection,
  // MenuItemCard,
  SubCategoryAccordion,
  CategoryNavBar,
  ImageZoomModal,
} from './sections';
import { SharedMenuItemCard } from '~/components/shared';

// Animation variants
const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const CATEGORY_ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 80,
      damping: 20,
    },
  },
};

interface RestaurantOnePageLayoutProps {
  searchMode?: boolean;
  filteredItems?: MenuItem[];
}

interface DisplayCategory extends MainCategory {
  directItems: MenuItem[];
  subcategoriesData: Array<{ id: string; items: MenuItem[]; [key: string]: unknown }>;
  displayItems: MenuItem[];
}

const RestaurantOnePageLayout: React.FC<RestaurantOnePageLayoutProps> = ({
  searchMode = false,
  filteredItems = [],
}) => {
  const { branchData, theme, categories, setSelectedItem } = useBranch();
  const { addToCart, addToCartWithVariants, setTempQuantity, getTempQuantity } = useShoppingCart();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  // Refs
  const navSentinelRef = useRef<HTMLDivElement | null>(null);
  const navScrollContainerRef = useRef<HTMLDivElement | null>(null);
  
  // Local state
  const [expandedSubCategories, setExpandedSubCategories] = useState<Record<string, boolean>>({});
  const [showImageModal, setShowImageModal] = useState(false);
  const [zoomedItem, setZoomedItem] = useState<MenuItem | null>(null);

  // Early returns
  if (!branchData) return null;
  if (branchData.branch.theme.isOnePageLayout !== true) return <Outlet />;

  // Memoize display categories calculation
  const displayCategories = React.useMemo((): DisplayCategory[] => {
    if (searchMode && filteredItems.length > 0) {
      const itemsByCategory = filteredItems.reduce((acc, item) => {
        const categoryId = item.category_id;
        if (!acc[categoryId]) acc[categoryId] = [];
        acc[categoryId].push(item);
        return acc;
      }, {} as Record<string, MenuItem[]>);

      return categories
        .filter((cat) => itemsByCategory[cat.id]?.length > 0)
        .map((category) => ({
          ...category,
          directItems: [],
          subcategoriesData: [],
          displayItems: itemsByCategory[category.id],
        }));
    }

    return categories
      .filter((cat) => {
        if (!cat.is_published) return false;
        const hasDirectItems = Array.isArray(cat.items) && cat.items.length > 0;
        const hasPublishedSubcategories = cat.subCategories.some(
          (sub) => sub.is_published && Array.isArray(sub.items) && sub.items.length > 0
        );
        return hasDirectItems || hasPublishedSubcategories;
      })
      .map((category) => {
        const directItems = (category.items || []).filter(
          (item) => item.is_published && item.is_available
        );
        const subcategoriesData = category.subCategories
          .filter((sub) => sub.is_published)
          .map((sub) => ({
            ...sub,
            items: (sub.items || []).filter((item) => item.is_published && item.is_available),
          }))
          .filter((sub) => sub.items.length > 0);

        return {
          ...category,
          directItems,
          subcategoriesData,
          displayItems: [...directItems, ...subcategoriesData.flatMap((sub) => sub.items)],
        };
      });
  }, [searchMode, filteredItems, categories]);

  // Custom hooks for scroll behavior
  const {
    activeCategory,
    isNavbarSticky,
    showScrollTop,
    setActiveCategory,
    isManualScroll,
    clearPendingCategory,
  } = useScrollDetection({
    categories: displayCategories as any,
    navSentinelRef,
  });

  const { scrollToTop, scrollToCategory, scrollToAll } = useScrollNavigation({
    isNavbarSticky,
    setActiveCategory,
    isManualScroll,
    clearPendingCategory,
  });

  const { handleMouseDown, handleMouseMove, handleMouseUp, isDragging } = useDragScroll({
    containerRef: navScrollContainerRef,
    onManualScrollStart: () => { isManualScroll.current = true; },
    onManualScrollEnd: () => { isManualScroll.current = false; },
  });

  // Auto-scroll category button into view when active category changes
  useEffect(() => {
    if (activeCategory && navScrollContainerRef.current && !isManualScroll.current) {
      const timer = setTimeout(() => {
        if (!isManualScroll.current) {
          const button = document.querySelector(
            `button[data-category-id="${activeCategory}"]`
          ) as HTMLElement;
          button?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [activeCategory, isManualScroll]);

  // Handlers
  const toggleSubCategory = useCallback((subCategoryId: string) => {
    setExpandedSubCategories((prev) => ({ ...prev, [subCategoryId]: !prev[subCategoryId] }));
  }, []);

  const handleZoom = useCallback((item: MenuItem) => {
    setZoomedItem(item);
    setShowImageModal(true);
  }, []);

  const handleAddToCart = useCallback((item: MenuItem) => {
    const directPayload = getDirectAddPayload(item);

    if (directPayload && branchData?.branch?.id) {
      // Build card data for session storage (CartModal reads from here)
      const cardId = `${item.id}-${Date.now()}`;
      const cardVariants = directPayload.variants.map((v) => ({
        name: v.optionName,
        children: [{
          name: v.variantName,
          price: v.variantPrice,
          quantity: v.quantity,
        }],
        quantity: v.quantity,
      }));

      const itemData = {
        itemId: item.id,
        itemName: getItemName(item, i18n.language),
        itemDescription: getItemDescription(item, i18n.language),
        itemImage: item.image_url || '',
        basePrice: item.discounted_price || item.price,
        totalQuantity: 1,
        cards: [{
          cardId,
          cardName: getItemName(item, i18n.language),
          quantity: 1,
          variants: cardVariants,
          isAiSuggestion: false,
        }],
        timestamp: Date.now(),
        isDraft: false, // Not a draft, directly added
      };

      // Save to session storage so CartModal sees it
      saveItemCardsToSession(item.id, itemData);

      // Update tempQuantities for the cart badge to show
      const currentQty = getTempQuantity(item.id);
      setTempQuantity(item.id, currentQty + 1, directPayload.variants);

      // Also update React state (for other cart functionality)
      if (directPayload.variants.length > 0) {
        addToCartWithVariants(item, branchData.branch.id, 1, directPayload.variants);
      } else {
        addToCart(item, branchData.branch.id, 1);
      }
    } else {
      // Needs manual selection - open modal
      setSelectedItem(item);
    }
  }, [branchData, addToCart, addToCartWithVariants, setSelectedItem, i18n.language, setTempQuantity, getTempQuantity]);

  // Empty state
  if (displayCategories.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-20 rounded-3xl max-w-lg mx-auto"
          style={{
            backgroundColor: `${theme?.primaryColor}10`,
            borderRadius: theme?.borderRadius,
          }}
        >
          <div className="text-7xl mb-6 flex justify-center" style={{ color: `${theme?.primaryColor}40` }}>
            <FaUtensils className="opacity-80" />
          </div>
          <h3 className="text-2xl font-bold mb-3" style={{ color: theme?.textColor }}>
            {searchMode ? t('menu.noSearchResults') : t('categories.noCategories')}
          </h3>
          <p className="max-w-md mx-auto" style={{ color: `${theme?.textColor}80` }}>
            {searchMode ? t('menu.noSearchResultsDescription') : t('categories.noCategoriesDescription')}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme?.backgroundColor }}>
      <div className="max-w-4xl mx-auto">
        {/* Navbar sentinel for sticky detection */}
        {!searchMode && <div ref={navSentinelRef} aria-hidden="true" className="h-0" />}

        {/* Category Navigation Bar */}
        {!searchMode && (
          <CategoryNavBar
            categories={displayCategories as any}
            activeCategory={activeCategory}
            isSticky={isNavbarSticky}
            theme={theme}
            containerRef={navScrollContainerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            isDragging={isDragging}
            onScrollToAll={scrollToAll}
            onScrollToCategory={scrollToCategory}
          />
        )}

        {/* Spacer for sticky navbar */}
        {!searchMode && isNavbarSticky && <div className="h-[60px]" aria-hidden="true" />}

        {/* Categories and Items */}
        <motion.div animate={{ paddingTop: '0rem' }}>
          <motion.div className="space-y-12" variants={CONTAINER_VARIANTS} initial="hidden" animate="visible">
            {displayCategories.map((category, index) => (
              <LazyCategorySection key={category.id} categoryId={category.id} index={index} theme={theme}>
                <motion.div variants={CATEGORY_ITEM_VARIANTS}>
                  {/* Category Image */}
                  <div
                    className="relative w-full aspect-[3/1] rounded-2xl overflow-hidden mb-6"
                    style={{ borderRadius: theme?.borderRadius }}
                  >
                    <img
                      src={category.image_url ?? '/images/no-image-available.jpg'}
                      alt={getCategoryName(category, i18n.language)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      onError={(e) => { (e.target as HTMLImageElement).src = '/images/broken-image.jpg'; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h2
                        className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2"
                        style={{ fontFamily: theme?.titleFontFamily || theme?.fontFamily, textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}
                      >
                        {getCategoryName(category, i18n.language)}
                      </h2>
                      {getCategoryDescription(category, i18n.language) && (
                        <p className="text-white/90 text-sm sm:text-base max-w-2xl" style={{ textShadow: '0 1px 5px rgba(0,0,0,0.5)' }}>
                          {getCategoryDescription(category, i18n.language)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Direct Items */}
                  {category.directItems.length > 0 && (
                    <div className="grid gap-4 mb-8">
                      {category.directItems.map((item) => (
                        <SharedMenuItemCard key={item.id} item={item} onZoom={handleZoom} onAdd={handleAddToCart} />
                      ))}
                    </div>
                  )}

                  {/* Subcategories */}
                  {!searchMode && category.subcategoriesData.length > 0 && (
                    <SubCategoryAccordion
                      subcategories={category.subcategoriesData}
                      categoryId={category.id}
                      expandedState={expandedSubCategories}
                      toggleSubCategory={toggleSubCategory}
                      onZoom={handleZoom}
                      onAdd={handleAddToCart}
                    />
                  )}

                  {/* Fallback for search mode */}
                  {category.directItems.length === 0 && category.subcategoriesData.length === 0 && category.displayItems.length > 0 && (
                    <div className="grid gap-4">
                      {category.displayItems.map((item) => (
                        <SharedMenuItemCard key={item.id} item={item} onZoom={handleZoom} onAdd={handleAddToCart} />
                      ))}
                    </div>
                  )}
                </motion.div>
              </LazyCategorySection>
            ))}
          </motion.div>
        </motion.div>

        {/* Bottom padding */}
        <div className="h-[50vh]" aria-hidden="true" />
      </div>

      {/* Scroll to top button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            onClick={scrollToTop}
            className={`fixed bottom-6 z-30 w-10 h-10 rounded-full shadow-lg flex items-center justify-center cursor-pointer ${isRTL ? 'right-6' : 'left-6'}`}
            whileHover={{ y: -4 }}
            whileTap={{ scale: 0.9 }}
            style={{ backgroundColor: theme?.primaryColor, color: '#fff', boxShadow: `0 4px 20px ${theme?.primaryColor}50` }}
          >
            <FaArrowUp />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Image Zoom Modal */}
      <ImageZoomModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        item={zoomedItem}
        defaultImage={branchData?.branch?.default_item_image ?? undefined}
        theme={theme}
      />
    </div>
  );
};

export default RestaurantOnePageLayout;
